import { Controller } from "../../lib/stimulus-shim"

// Controller spécialisé pour les transitions entre pages de profil avec effet zoom circulaire
// Namespace: profiles - pour les vues publiques des profils
export default class extends Controller {
  static targets = ["content", "navigation"]
  static values = {
    pages: Array,
    currentPagePosition: Number,
    style: { type: String, default: 'fade' },
    duration: { type: Number, default: 300 }
  }

  connect() {
    console.log("Profiles::PageTransition controller connected", {
      pages: this.pagesValue,
      currentPosition: this.currentPagePositionValue,
      style: this.styleValue,
      duration: this.durationValue
    })

    // Vérifier que le target content existe avant de continuer
    if (!this.hasContentTarget) {
      console.warn("profiles--page-transition: content target missing, controller will not initialize")
      return
    }

    this.setupKeyboardNavigation()
    this.animateInitialLoad()
    this.updateNavigationDots()

    // Appliquer la durée d'animation via CSS variable
    this.applyAnimationSettings()
    this.showSwipeHint()
  }

  // Peek au chargement : léger décalage du contenu pour suggérer le swipe.
  // Une fois par session seulement ; le tactile seul est géré côté CSS
  // (@media pointer: coarse sur l'animation).
  showSwipeHint() {
    if (this.pagesValue.length < 2) return
    if (sessionStorage.getItem('swipeHinted')) return

    sessionStorage.setItem('swipeHinted', '1')
    this.contentTarget.classList.add('profile-swipe-hint')
  }

  disconnect() {
    this.removeKeyboardNavigation()
  }

  // Navigation vers une page spécifique (depuis les dots de navigation)
  navigateToPage(event) {
    const url = event.params.url
    const targetPosition = parseInt(event.params.position)

    // Params absents (attribut mal formé) : laisser la navigation par défaut
    // plutôt que d'animer vers nulle part (page fantôme à opacité 0)
    if (!url) return

    event.preventDefault()

    if (targetPosition === this.currentPagePositionValue) {
      return // Déjà sur cette page
    }

    this.performZoomTransition(url, targetPosition)
  }

  // Navigation par swipe ou clavier
  navigateToPrevious() {
    const currentIndex = this.getCurrentPageIndex()
    const prevIndex = currentIndex - 1
    
    if (prevIndex >= 0) {
      const prevPage = this.pagesValue[prevIndex]
      const url = this.getUrlForPage(prevPage)
      this.performZoomTransition(url, prevPage.position)
    }
  }

  navigateToNext() {
    const currentIndex = this.getCurrentPageIndex()
    const nextIndex = currentIndex + 1
    
    if (nextIndex < this.pagesValue.length) {
      const nextPage = this.pagesValue[nextIndex]
      const url = this.getUrlForPage(nextPage)
      this.performZoomTransition(url, nextPage.position)
    }
  }

  // Gestion des swipes tactiles (appelé depuis le script global dans le layout)
  swipeLeft() {
    this.navigateToNext()
  }

  swipeRight() {
    this.navigateToPrevious()
  }

  // Appliquer les settings d'animation
  applyAnimationSettings() {
    if (!this.hasContentTarget) return

    const content = this.contentTarget

    // Appliquer le style d'animation via data attribute
    content.dataset.animationStyle = this.styleValue

    // Appliquer la durée via CSS variable si pas déjà sur body
    if (!document.body.style.getPropertyValue('--animation-duration')) {
      document.body.style.setProperty('--animation-duration', `${this.durationValue}ms`)
    }
  }

  // Effectuer la transition avec le style configuré
  performZoomTransition(url, newPosition) {
    if (!this.hasContentTarget) {
      Turbo.visit(url, { action: "replace" })
      return
    }

    const content = this.contentTarget

    // Bloquer les nouvelles transitions pendant celle-ci
    if (this.isTransitioning) return
    this.isTransitioning = true

    console.log(`Starting ${this.styleValue} transition to page ${newPosition}`)

    // Ne rien faire si style = 'none'
    if (this.styleValue === 'none') {
      Turbo.visit(url, { action: "replace" })
      return
    }

    // Phase 1: Animation de sortie
    content.classList.add(`profile-page-transition-exit-${this.styleValue}`)

    // Phase 2: Navigation après l'animation de sortie
    setTimeout(() => {
      Turbo.visit(url, { action: "replace" })
    }, this.durationValue)
  }

  // Animation d'entrée lors du chargement initial
  animateInitialLoad() {
    if (!this.hasContentTarget) return

    const content = this.contentTarget

    // Ne rien faire si style = 'none'
    if (this.styleValue === 'none') return

    // Ajouter une petite pause pour que le DOM soit prêt
    requestAnimationFrame(() => {
      content.classList.add(`profile-page-transition-enter-${this.styleValue}`)

      // Déclencher l'animation d'entrée
      setTimeout(() => {
        content.classList.remove(`profile-page-transition-enter-${this.styleValue}`)
        content.classList.add(`profile-page-transition-enter-active-${this.styleValue}`)
      }, 50)
    })
  }

  // Mise à jour des dots de navigation
  updateNavigationDots() {
    if (!this.hasNavigationTarget) return

    const dots = this.navigationTarget.querySelectorAll('.nav-dot')
    const currentIndex = this.getCurrentPageIndex()
    
    dots.forEach((dot, index) => {
      if (index === currentIndex) {
        dot.classList.add('active')
      } else {
        dot.classList.remove('active')
      }
    })
  }

  // Navigation clavier
  setupKeyboardNavigation() {
    this.keyHandler = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.keyHandler)
  }

  removeKeyboardNavigation() {
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler)
    }
  }

  handleKeydown(event) {
    // Éviter d'intercepter si l'utilisateur tape dans un input
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return
    }

    switch(event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        this.navigateToPrevious()
        break
      case 'ArrowRight':
        event.preventDefault()
        this.navigateToNext()
        break
      case 'Home':
        event.preventDefault()
        // Naviguer vers la page d'accueil (position 1)
        if (this.pagesValue.length > 0 && this.currentPagePositionValue !== 1) {
          const firstPage = this.pagesValue.find(p => p.position === 1) || this.pagesValue[0]
          const url = this.getUrlForPage(firstPage)
          this.performZoomTransition(url, firstPage.position)
        }
        break
    }
  }

  // Utilitaires
  getCurrentPageIndex() {
    return this.pagesValue.findIndex(page => page.position === this.currentPagePositionValue)
  }

  getUrlForPage(page) {
    // Extraire le profile_slug de l'URL actuelle
    const pathParts = window.location.pathname.split('/')
    const profileSlug = pathParts[1] // Premier segment après /
    
    if (page.position === 1) {
      // Page d'accueil = position 1 (cf. ProfilesController#show) -> URL racine sans slug
      return `/${profileSlug}`
    } else {
      // Page avec slug
      return `/${profileSlug}/${page.slug}`
    }
  }

  // Callbacks pour les changements de valeurs Stimulus
  pagesValueChanged() {
    console.log("Pages updated:", this.pagesValue)
    this.updateNavigationDots()
  }

  currentPagePositionValueChanged() {
    console.log("Current page position changed:", this.currentPagePositionValue)
    this.updateNavigationDots()
    this.isTransitioning = false // Permettre de nouvelles transitions
  }

  // Méthode appelée quand Turbo charge une nouvelle page
  // (utilisée via l'événement turbo:load dans le layout)
  handleTurboLoad() {
    if (!this.hasContentTarget) return

    // Nettoyer les états de transition précédents pour tous les styles
    const content = this.contentTarget
    const styles = ['fade', 'slide', 'zoom']
    styles.forEach(style => {
      content.classList.remove(
        `profile-page-transition-exit-${style}`,
        `profile-page-transition-enter-${style}`,
        `profile-page-transition-enter-active-${style}`
      )
    })

    // Lancer l'animation d'entrée
    this.animateInitialLoad()

    // Permettre de nouvelles transitions
    this.isTransitioning = false
  }
}

// Attacher le gestionnaire d'événements Turbo globalement
document.addEventListener('turbo:load', () => {
  const controller = document.querySelector('[data-controller*="profiles--page-transition"]')
  if (controller && window.Stimulus) {
    const controllerInstance = window.Stimulus.getControllerForElementAndIdentifier(controller, 'profiles--page-transition')
    if (controllerInstance && controllerInstance.handleTurboLoad) {
      controllerInstance.handleTurboLoad()
    }
  }
})